import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ThunkAction } from 'redux-thunk'
import { AppThunk, RootState } from '../store'
import PatientDiagnosisDiscussions from '../model/PatientsDiagnosisDiscussions'
import PatientDiagnosisRepository, {
  TestOrderNote,
} from './appointment/PatientDiagnosisRepository'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import PatientsDiagnosisMedications from 'model/PatientsDiagnosisMedications'
import PatientsDiagnosis from 'model/PatientsDiagnosis'
import PatientsDiagnosisSimple from 'model/PatientsDiagnosisSimple'
import PatientFile from 'model/PatientFile'
import Allergy from 'model/Allergy'
import { ExamResultStatus, setExaminationDocument } from 'model/Examination'
import { ExaminationStatus } from 'model/PatientFile'
import InternalExaminationPlan from 'model/InternalExaminationPlan'

/** Must match the value `findAllPatientsDiscussions` counts server-side for `unreviewedCount`. */
const UNREVIEWED_FOLLOW_UP_STATUS = ExaminationStatus.notViewdTestResult

/**
 * One page of follow-ups plus the cursor state the next request needs.
 *
 * `paged` says which array the reply belongs in. An unpaged fetch asked for the patient's whole
 * list and feeds `discussions`; a paged one is the workspace's filtered page and feeds
 * `workspaceDiscussions`. Both go through the same action, so without this the workspace's page
 * would overwrite the full list that the rest of the app reads.
 */
interface DiscussionsPage {
  discussions: PatientDiagnosisDiscussions[]
  bookmark: number
  hasMore: boolean
  unreviewedCount: number
  totalCount: number
  paged?: boolean
}

/** One page of examinations plus the cursor state the next request needs. See `DiscussionsPage`. */
interface ExaminationsPage {
  examinations: any[]
  bookmark: number
  hasMore: boolean
  totalCount: number
  /** Every tab's count for the whole patient, present on a tab-filtered reply. */
  statusCounts?: Record<ExamResultStatus, number>
  /** The tab the page was requested for, so a reply for a tab the user has left is dropped. */
  status?: ExamResultStatus
  paged?: boolean
}

interface PatientDiagnosisState {
  /**
   * The patient everything below belongs to, set by `setActivePatient` when a patient screen opens.
   *
   * Without it the slice could not tell whose data it held: switching patient left the previous
   * one's lists on screen until each fetch landed, and a slow reply for the previous patient could
   * land after the new one's and overwrite it. Null outside a patient screen, where nothing is
   * filtered — screens like the clinic documents list open one patient's file without setting it.
   */
  patientId: string | null
  isLoadingTopics: boolean
  isLoadingExaminations: boolean
  examinationsLoaded: boolean
  isLoading: boolean
  topics: PatientsDiagnosisTopics[]
  diagnosis: PatientsDiagnosis[]
  discussions: PatientDiagnosisDiscussions[]
  medications: PatientsDiagnosisMedications[]
  allergies: Allergy[]
  examinations: any[]
  medicalReferralResults: PatientFile[]
  appointmentOwner: { _id: any; firstName: string; lastName: string } | null
  schedulePlans: InternalExaminationPlan[]
  /**
   * The workspace's paged view of the follow-ups, kept apart from `discussions`.
   *
   * These are two different things and conflating them was a bug: `discussions` is every follow-up
   * the patient has, which `VdgDiagnosisTopic` and the leave-prompt read in full, while this is one
   * filtered page of them. Paging the shared array meant the workspace silently truncated it for
   * every other consumer.
   *
   * Still a flat array rather than a list of pages: appending keeps `updatePatientDiscussionsSuccess`
   * splicing edits in place, so only the workspace has to know paging happened.
   */
  workspaceDiscussions: PatientDiagnosisDiscussions[]
  /** Paging cursor for `workspaceDiscussions`. */
  discussionsBookmark: number
  discussionsHasMore: boolean
  /**
   * Unreviewed follow-ups for the whole patient, counted server-side. The workspace's leave-prompt
   * is a safety check — it must not stop warning because the unreviewed one sits on an unloaded page.
   */
  discussionsUnreviewedCount: number
  /** Follow-ups the patient has in total, not the number loaded — what the "(n)" headings show. */
  discussionsTotalCount: number
  /**
   * In-flight flag for follow-up paging, separate from the slice-wide `isLoading`.
   *
   * `isLoading` is written by every reducer here, so a sibling fetch settling mid-request cleared
   * it, re-armed the scroll sentinel and pulled the same page twice.
   */
  discussionsPageLoading: boolean
  /**
   * The workspace's paged view of the examinations, kept apart from `examinations` for the same
   * reason as the follow-ups above — and a sharper one. `getActiveHospitalization` scans the whole
   * array for the admission burst, and `VdgLabTestsList` counts unreviewed labs across it; against
   * a truncated array the admission banner simply stops rendering, with nothing to indicate why.
   */
  workspaceExaminations: any[]
  /** Paging state for `workspaceExaminations`, mirroring the follow-ups above. */
  examinationsBookmark: number
  examinationsHasMore: boolean
  /** Examinations in the active tab for the whole patient, not the number loaded. */
  examinationsTotalCount: number
  /**
   * The tab `workspaceExaminations` holds, or null when no workspace is showing it. The rows are one
   * tab, filtered server-side — sorting a loaded page into tabs in the browser was the RD-53 bug:
   * badges counted only loaded rows and later-page rows of a tab were unreachable.
   */
  workspaceExaminationsStatus: ExamResultStatus | null
  /** Every tab's count for the whole patient, counted server-side — what the tab badges show. */
  examinationsStatusCounts: Record<ExamResultStatus, number> | null
  /** In-flight flag for examination paging, separate from the slice-wide `isLoadingExaminations`. */
  examinationsPageLoading: boolean
}

const initialState: PatientDiagnosisState = {
  patientId: null,
  isLoadingTopics: true,
  isLoadingExaminations: true,
  examinationsLoaded: false,
  isLoading: false,
  topics: [] as PatientsDiagnosisTopics[],
  diagnosis: [] as PatientsDiagnosis[],
  discussions: [] as PatientDiagnosisDiscussions[],
  medications: [] as PatientsDiagnosisMedications[],
  allergies: [] as Allergy[],
  examinations: [],
  medicalReferralResults: [],
  appointmentOwner: null,
  schedulePlans: [],
  workspaceDiscussions: [],
  discussionsBookmark: 0,
  discussionsHasMore: false,
  discussionsUnreviewedCount: 0,
  discussionsTotalCount: 0,
  discussionsPageLoading: false,
  workspaceExaminations: [],
  examinationsBookmark: 0,
  examinationsHasMore: false,
  examinationsTotalCount: 0,
  workspaceExaminationsStatus: null,
  examinationsStatusCounts: null,
  examinationsPageLoading: false,
}

function startLoading(state: PatientDiagnosisState) {
  state.isLoading = true
}

function startLoadingTopics(state: PatientDiagnosisState) {
  state.isLoadingTopics = true
}

/** True when `item` belongs to a patient other than the active one, so it must not be stored. */
function isOtherPatients(state: PatientDiagnosisState, item?: { patientId?: string } | null) {
  return !!state.patientId && !!item?.patientId && item.patientId !== state.patientId
}

/** Same check for a fetch thunk, made when its reply arrives rather than when it was sent. */
function isStaleReply(getState: () => RootState, patientId: string) {
  const activePatientId = getState().patientDiagnosis.patientId
  return !!activePatientId && activePatientId !== patientId
}

const patientDiagnosisSlice = createSlice({
  name: 'patientDiagnosis',
  initialState,
  reducers: {
    // A different patient starts from a clean slice, so the previous one's data is never shown
    // under the new name while the fetches are in flight.
    setActivePatient(state, { payload }: PayloadAction<string | null>) {
      if (state.patientId === payload) return
      return { ...initialState, patientId: payload }
    },
    fetchPatientTopicsStart: startLoadingTopics,
    fetchPatientDiagnosisStart: startLoading,
    // Sets the dedicated flag too, so the sentinel has a signal only follow-up requests can clear.
    fetchPatientDiscussionsStart(state: PatientDiagnosisState) {
      state.isLoading = true
      state.discussionsPageLoading = true
    },
    fetchPatientMedicationsStart: startLoading,
    fetchPatientAllergiesStart: startLoading,
    fetchPatientExaminationsStart(state: PatientDiagnosisState) {
      state.isLoadingExaminations = true
      state.examinationsPageLoading = true
    },
    fetchPatientMedicalReferralResultsStart: startLoading,
    createPatientTopicStart: startLoading,
    updatePatientTopicStart: startLoading,
    fetchPatientTopicsSuccess(state, { payload }: PayloadAction<PatientsDiagnosisTopics[]>) {
      state.topics = payload
      state.isLoading = false
      state.isLoadingTopics = false
    },
    fetchPatientDiagnosisSuccess(state, { payload }: PayloadAction<PatientsDiagnosis[]>) {
      state.diagnosis = payload
      state.isLoading = false
    },
    // First page: replaces whatever was loaded for the previous patient — or for the previous tab,
    // since switching review filter re-fetches from page 0.
    //
    // Writes `workspaceDiscussions` only. `discussions` is fed by the unpaged fetch and must keep
    // holding the patient's whole list for the screens that read it in full.
    fetchPatientDiscussionsSuccess(state, { payload }: PayloadAction<DiscussionsPage>) {
      if (payload.paged) {
        state.workspaceDiscussions = payload.discussions
        // Cursor fields describe the paged list only — see the note in the examinations reducer.
        state.discussionsBookmark = payload.bookmark
        state.discussionsHasMore = payload.hasMore
      } else {
        state.discussions = payload.discussions
      }
      // Counts are of the whole patient either way, so both replies carry the current figures.
      state.discussionsUnreviewedCount = payload.unreviewedCount
      state.discussionsTotalCount = payload.totalCount
      state.isLoading = false
      state.discussionsPageLoading = false
    },
    fetchPatientDiscussionsNextPageSuccess(state, { payload }: PayloadAction<DiscussionsPage>) {
      // Append only what is not already held: the in-flight flag races a scroll event, and losing
      // that race would show a clinician the same follow-up twice.
      const known = new Set(state.workspaceDiscussions.map(d => (d as any)._id))
      const fresh = payload.discussions.filter(d => !known.has((d as any)._id))

      state.workspaceDiscussions = [...state.workspaceDiscussions, ...fresh]
      state.discussionsBookmark = payload.bookmark
      state.discussionsHasMore = payload.hasMore
      state.discussionsUnreviewedCount = payload.unreviewedCount
      state.discussionsTotalCount = payload.totalCount
      state.isLoading = false
      state.discussionsPageLoading = false
    },
    fetchPatientMedicationsSuccess(
      state,
      { payload }: PayloadAction<PatientsDiagnosisMedications[]>,
    ) {
      state.medications = payload
      state.isLoading = false
    },
    fetchPatientAllergiesSuccess(state, { payload }: PayloadAction<Allergy[]>) {
      state.allergies = payload
      state.isLoading = false
    },
    // First page: replaces, so switching patient does not stack onto the previous one's rows.
    fetchPatientExaminationsSuccess(state, { payload }: PayloadAction<ExaminationsPage>) {
      if (payload.paged) {
        // A reply for a tab the user has since left would put that tab's rows under this one.
        if (payload.status === state.workspaceExaminationsStatus) {
          state.workspaceExaminations = payload.examinations
          // Cursor fields describe the paged list only. An unpaged reply carries `bookmark: 1` and
          // `hasMore: false`, so writing them from one would rewind the workspace's cursor and hide
          // its load-more control.
          state.examinationsBookmark = payload.bookmark
          state.examinationsHasMore = payload.hasMore
          state.examinationsTotalCount = payload.totalCount
          state.examinationsStatusCounts = payload.statusCounts ?? null
        }
      } else {
        // The workspace's rows are not refreshed from this: they are one server-filtered tab, and
        // the whole list cannot be cut down to that without re-implementing the server's sort. The
        // `fetchPatientExaminations` thunk re-requests the workspace's page instead.
        state.examinations = payload.examinations
      }
      state.isLoading = false
      state.isLoadingExaminations = false
      state.examinationsPageLoading = false
      state.examinationsLoaded = true
    },
    fetchPatientExaminationsNextPageSuccess(state, { payload }: PayloadAction<ExaminationsPage>) {
      // The user switched tab while this page was in flight — it belongs to the old tab.
      if (payload.status !== state.workspaceExaminationsStatus) {
        state.examinationsPageLoading = false
        return
      }

      // Same de-duplication as the follow-ups.
      const known = new Set(state.workspaceExaminations.map(e => (e as any)._id))
      const fresh = payload.examinations.filter(e => !known.has((e as any)._id))

      state.workspaceExaminations = [...state.workspaceExaminations, ...fresh]
      state.examinationsBookmark = payload.bookmark
      state.examinationsHasMore = payload.hasMore
      state.examinationsTotalCount = payload.totalCount
      state.examinationsStatusCounts = payload.statusCounts ?? state.examinationsStatusCounts
      state.isLoading = false
      state.isLoadingExaminations = false
      state.examinationsPageLoading = false
      state.examinationsLoaded = true
    },
    fetchPatientExaminationsPageFailed(state: PatientDiagnosisState) {
      state.isLoading = false
      state.isLoadingExaminations = false
      state.examinationsPageLoading = false
    },
    /**
     * Points the workspace's examination list at a tab, or detaches it (null) when the workspace
     * unmounts. Changing tab empties the rows at once, so the old tab's rows are never shown under
     * the new tab's heading while its first page loads.
     */
    setWorkspaceExaminationsStatus(
      state: PatientDiagnosisState,
      { payload }: PayloadAction<ExamResultStatus | null>,
    ) {
      if (payload === state.workspaceExaminationsStatus) return
      state.workspaceExaminationsStatus = payload
      state.workspaceExaminations = []
      state.examinationsBookmark = 0
      state.examinationsHasMore = false
      if (payload === null) state.examinationsStatusCounts = null
    },
    fetchPatientMedicalReferralResultsSuccess(state, { payload }: PayloadAction<PatientFile[]>) {
      state.medicalReferralResults = payload
      state.isLoading = false
    },
    fetchPatientSchedulePlansSuccess(state, { payload }: PayloadAction<InternalExaminationPlan[]>) {
      state.schedulePlans = payload
      state.isLoading = false
    },
    fetchPatientAppointmentOwnerSuccess(state, { payload }: PayloadAction<any>) {
      state.appointmentOwner = payload
    },
    createPatientTopicSuccess(state, { payload }) {
      if (isOtherPatients(state, payload?.topic)) {
        state.isLoading = false
        return
      }
      state.topics = [payload?.topic, ...state.topics]
      state.diagnosis = [payload?.mainDiagnosis, ...state.diagnosis]
      state.isLoading = false
    },
    createPatientSchedulePlanSuccess(state, { payload }) {
      if (isOtherPatients(state, payload?.schedulePlan)) {
        state.isLoading = false
        return
      }
      state.schedulePlans = [payload?.schedulePlan, ...state.schedulePlans]
      state.isLoading = false
    },
    updatePatientTopicSuccess(state, { payload }) {
      state.isLoading = false
      const objIndex = state.topics.findIndex(
        topic => (topic as any)._id === payload?.updatedTopic._id,
      )
      state.topics = [
        ...state.topics.slice(0, objIndex),
        payload.updatedTopic,
        ...state.topics.slice(objIndex + 1),
      ]
      if (payload.newDiagnosis) state.diagnosis = [...state.diagnosis, payload?.newDiagnosis]
    },
    deleteTopicSuccess(state, { payload }) {
      const filteredTopics = state.topics.filter(item => item._id !== payload.id)
      state.topics = filteredTopics
    },
    // This and the upserts below run after a write that can outlive the screen it started on, so a
    // reply for the previous patient is dropped rather than prepended to the current one's list.
    updatePatientDiscussionsSuccess(state, { payload }) {
      state.isLoading = false
      if (isOtherPatients(state, payload)) return

      const objIndex = state.discussions.findIndex(
        discussion => (discussion as any)._id === payload?._id,
      )

      // Read before the list is reassigned — afterwards this index holds the new row.
      const wasUnreviewed =
        objIndex >= 0 &&
        (state.discussions[objIndex] as any)?.examinationStatus === UNREVIEWED_FOLLOW_UP_STATUS

      let newDiscussionList =
        objIndex >= 0
          ? [
              ...state.discussions.slice(0, objIndex),
              payload,
              ...state.discussions.slice(objIndex + 1),
            ]
          : [payload, ...state.discussions]

      state.discussions = newDiscussionList

      // The workspace renders its own paged array, so the same edit has to land there or marking a
      // follow-up reviewed would leave the row visibly unchanged on the screen it was done from.
      // A new follow-up is prepended only if that array is in use — on a patient whose first page
      // has not loaded, prepending would be the one row shown under an unrelated total.
      const pagedIndex = state.workspaceDiscussions.findIndex(
        discussion => (discussion as any)._id === payload?._id,
      )

      if (pagedIndex >= 0) {
        state.workspaceDiscussions = [
          ...state.workspaceDiscussions.slice(0, pagedIndex),
          payload,
          ...state.workspaceDiscussions.slice(pagedIndex + 1),
        ]
      } else if (state.workspaceDiscussions.length > 0 && objIndex < 0) {
        state.workspaceDiscussions = [payload, ...state.workspaceDiscussions]
      }

      // A follow-up that was not already in the list is a new one, so it adds to the total.
      if (objIndex < 0) {
        state.discussionsTotalCount += 1
      }

      // Keep the server-counted total in step with edits here, or the leave-prompt never clears and
      // traps the user on the page. Only the transition matters.
      const isUnreviewed = payload?.examinationStatus === UNREVIEWED_FOLLOW_UP_STATUS

      if (wasUnreviewed && !isUnreviewed) {
        state.discussionsUnreviewedCount = Math.max(0, state.discussionsUnreviewedCount - 1)
      } else if (!wasUnreviewed && isUnreviewed) {
        state.discussionsUnreviewedCount += 1
      }
    },
    updatePatientMedicasionsSuccess(state, { payload }) {
      state.isLoading = false
      if (isOtherPatients(state, payload)) return

      const objIndex = state.medications.findIndex(
        medication => (medication as any)._id === payload?._id,
      )

      let newMedicationsList =
        objIndex >= 0
          ? [
              ...state.medications.slice(0, objIndex),
              payload,
              ...state.medications.slice(objIndex + 1),
            ]
          : [payload, ...state.medications]

      state.medications = newMedicationsList
    },
    updatePatientAllergiesSuccess(state, { payload }) {
      state.isLoading = false
      if (isOtherPatients(state, payload)) return

      const objIndex = state.allergies.findIndex(allergy => (allergy as any)._id === payload?._id)

      let newAllergiesList =
        objIndex >= 0
          ? [...state.allergies.slice(0, objIndex), payload, ...state.allergies.slice(objIndex + 1)]
          : [payload, ...state.allergies]

      state.allergies = newAllergiesList
    },
    updatePatientSchedulePlanSuccess(state, { payload }) {
      state.isLoading = false
      if (isOtherPatients(state, payload)) return

      const objIndex = state.schedulePlans.findIndex(
        schedulePlan => (schedulePlan as any)._id === payload?._id,
      )

      let newSchedulePlan =
        objIndex >= 0
          ? [
              ...state.schedulePlans.slice(0, objIndex),
              payload,
              ...state.schedulePlans.slice(objIndex + 1),
            ]
          : [payload, ...state.schedulePlans]

      state.schedulePlans = newSchedulePlan
    },
    // updatePatientExaminationsSuccess(state, { payload }) {
    //   state.isLoading = false
    //   state.examinations = [payload, ...state.examinations]
    // },
    updatePatientFailed(state: PatientDiagnosisState) {
      state.isLoading = false
    },
    fetchPatientDiscussionsPageFailed(state: PatientDiagnosisState) {
      state.isLoading = false
      state.discussionsPageLoading = false
    },
  },
})

export const {
  setActivePatient,
  fetchPatientTopicsStart,
  fetchPatientDiagnosisStart,
  fetchPatientDiscussionsStart,
  fetchPatientMedicationsStart,
  fetchPatientAllergiesStart,
  fetchPatientExaminationsStart,
  fetchPatientSchedulePlansSuccess,
  fetchPatientMedicalReferralResultsStart,
  createPatientTopicStart,
  updatePatientTopicStart,
  updatePatientSchedulePlanSuccess,
  fetchPatientTopicsSuccess,
  fetchPatientDiagnosisSuccess,
  fetchPatientDiscussionsSuccess,
  fetchPatientDiscussionsNextPageSuccess,
  fetchPatientMedicationsSuccess,
  fetchPatientAllergiesSuccess,
  fetchPatientExaminationsSuccess,
  fetchPatientExaminationsNextPageSuccess,
  fetchPatientExaminationsPageFailed,
  setWorkspaceExaminationsStatus,
  fetchPatientMedicalReferralResultsSuccess,
  fetchPatientAppointmentOwnerSuccess,
  createPatientTopicSuccess,
  createPatientSchedulePlanSuccess,
  updatePatientTopicSuccess,

  updatePatientDiscussionsSuccess,
  updatePatientMedicasionsSuccess,
  updatePatientAllergiesSuccess,
  deleteTopicSuccess,
  // updatePatientExaminationsSuccess,
  updatePatientFailed,
  fetchPatientDiscussionsPageFailed,
} = patientDiagnosisSlice.actions

// Every fetch below checks `isStaleReply` once its reply is in: the user may have moved to another
// patient while it was in flight, and that patient's own fetch may already have landed.

export const fetchPatientTopics = (patientId: string): AppThunk => async (dispatch, getState) => {
  dispatch(fetchPatientTopicsStart())
  const topics = await PatientDiagnosisRepository.findAllPatientTopics({ patientId })
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientTopicsSuccess(topics))
}

export const fetchPatientDiagnosis = (patientId: string): AppThunk => async (
  dispatch,
  getState,
) => {
  dispatch(fetchPatientDiagnosisStart())
  const diagnosis = await PatientDiagnosisRepository.findAllPatientDiagnosis({ patientId })
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientDiagnosisSuccess(diagnosis))
}

/** Page size for follow-ups, used only where there is a way to ask for the next page. */
export const DISCUSSIONS_PAGE_LIMIT = 30

/**
 * Loads the first page of follow-ups.
 *
 * `limit` is opt-in because only the workspace has a load-more control — `VdgDiagnosisTopic` renders
 * the same list without one, so limiting it there would strand rows with no way to reach them.
 *
 * `reviewFilter` narrows the page server-side. The workspace re-fetches from page 0 when the user
 * switches tab rather than keeping a cursor per tab: the Unreviewed list is short by nature, and
 * one cursor means the loaded rows always match the tab the user is looking at.
 */
export const fetchPatientDiscussions = (
  patientId: string,
  limit?: number,
  reviewFilter?: 'unreviewed',
): AppThunk => async (dispatch, getState) => {
  dispatch(fetchPatientDiscussionsStart())
  try {
    const page = await PatientDiagnosisRepository.findAllPatientDiscussions({
      patientId,
      bookmark: 0,
      limit,
      reviewFilter,
    })
    if (isStaleReply(getState, patientId)) return

    dispatch(fetchPatientDiscussionsSuccess({ ...page, paged: !!limit }))
  } catch {
    dispatch(fetchPatientDiscussionsPageFailed())
  }
}

export const fetchPatientDiscussionsNextPage = (
  patientId: string,
  bookmark: number,
  reviewFilter?: 'unreviewed',
): AppThunk => async (dispatch, getState) => {
  dispatch(fetchPatientDiscussionsStart())
  try {
    const page = await PatientDiagnosisRepository.findAllPatientDiscussions({
      patientId,
      bookmark,
      limit: DISCUSSIONS_PAGE_LIMIT,
      reviewFilter,
    })
    if (isStaleReply(getState, patientId)) return

    dispatch(fetchPatientDiscussionsNextPageSuccess(page))
  } catch {
    // Must clear even on failure, or the sentinel stays disarmed and the list stalls. `hasMore` is
    // left alone so the footer's button stays on screen for a retry.
    dispatch(fetchPatientDiscussionsPageFailed())
  }
}

export const fetchPatientMedications = (patientId: string): AppThunk => async (
  dispatch,
  getState,
) => {
  dispatch(fetchPatientMedicationsStart())
  const medications = await PatientDiagnosisRepository.findAllPatientMedications({ patientId })
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientMedicationsSuccess(medications))
}

export const fetchPatientAllergies = (patientId: string): AppThunk => async (
  dispatch,
  getState,
) => {
  dispatch(fetchPatientAllergiesStart())
  const allergies = await PatientDiagnosisRepository.findAllPatientAllergies({ patientId })
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientAllergiesSuccess(allergies))
}

/** Page size for examinations. Same opt-in rule as the follow-ups: only the workspace can page. */
export const EXAMINATIONS_PAGE_LIMIT = 20

/**
 * Loads the patient's examinations: the whole list, or — with `limit` and `status` — the first page
 * of one workspace tab.
 *
 * Every write to an examination (ordering, marking reviewed, a lab value) refetches through here
 * unpaged. So after an unpaged load, if the workspace is showing this patient, its tab is
 * re-requested too, or the write would not appear there: a row marked reviewed would stay under
 * Unreviewed. That restarts the tab from page 0, since the edit can move rows between tabs.
 */
export const fetchPatientExaminations = (
  patientId: string,
  limit?: number,
  status?: ExamResultStatus,
): AppThunk => async (dispatch, getState) => {
  const paged = !!limit
  if (paged && status) dispatch(setWorkspaceExaminationsStatus(status))
  dispatch(fetchPatientExaminationsStart())
  try {
    const page = await PatientDiagnosisRepository.findAllPatientExaminations({
      patientId,
      bookmark: 0,
      limit,
      status,
    })
    if (isStaleReply(getState, patientId)) return

    dispatch(
      fetchPatientExaminationsSuccess({
        ...page,
        paged,
        status,
        examinations: page.examinations.map(e => setExaminationDocument(e)),
      }),
    )
  } catch {
    dispatch(fetchPatientExaminationsPageFailed())
    return
  }

  if (paged) return
  const { patientDiagnosis, patient } = getState()
  const workspaceStatus = patientDiagnosis.workspaceExaminationsStatus
  if (workspaceStatus && patient.patient?.id === patientId) {
    dispatch(fetchPatientExaminations(patientId, EXAMINATIONS_PAGE_LIMIT, workspaceStatus))
  }
}

export const fetchPatientExaminationsNextPage = (
  patientId: string,
  bookmark: number,
  status: ExamResultStatus,
): AppThunk => async (dispatch, getState) => {
  dispatch(fetchPatientExaminationsStart())
  try {
    const page = await PatientDiagnosisRepository.findAllPatientExaminations({
      patientId,
      bookmark,
      limit: EXAMINATIONS_PAGE_LIMIT,
      status,
    })
    if (isStaleReply(getState, patientId)) return

    dispatch(
      fetchPatientExaminationsNextPageSuccess({
        ...page,
        status,
        examinations: page.examinations.map(e => setExaminationDocument(e)),
      }),
    )
  } catch {
    dispatch(fetchPatientExaminationsPageFailed())
  }
}

export const fetchPatientSchedulePlans = (patientId: string): AppThunk => async (
  dispatch,
  getState,
) => {
  let schedulePlans = await PatientDiagnosisRepository.findAllPatientSchedulePlans({ patientId })
  if (isStaleReply(getState, patientId)) return
  dispatch(fetchPatientSchedulePlansSuccess(schedulePlans))
}

export const fetchPatientMedicalReferralResults = (
  patientId: string,
): AppThunk => async (dispatch, getState) => {
  dispatch(fetchPatientMedicalReferralResultsStart())
  const medicalReferralResults = await PatientDiagnosisRepository.findAllPatientMedicalReferralResults(
    { patientId },
  )
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientMedicalReferralResultsSuccess(medicalReferralResults))
}

export const fetchPatientAppointmentOwner = (patientId: string): AppThunk => async (
  dispatch,
  getState,
) => {
  const appointmentOwner = await PatientDiagnosisRepository.getAppointmentOwner({ patientId })
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientAppointmentOwnerSuccess(appointmentOwner))
}

export const updatePatientAppointmentOwner = (
  patientId: string,
  userId: string | null,
): AppThunk => async (dispatch, getState) => {
  const appointmentOwner = await PatientDiagnosisRepository.setAppointmentOwner(patientId, userId)
  if (isStaleReply(getState, patientId)) return

  dispatch(fetchPatientAppointmentOwnerSuccess(appointmentOwner))
}

export const createPatientTopic = (
  topic: PatientsDiagnosisTopics,
  firstDiagnosis: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(createPatientTopicStart())

  const result = await PatientDiagnosisRepository.createTopic(topic, firstDiagnosis)
  dispatch(createPatientTopicSuccess(result))
}

/** With `note`, also writes the order's progress note (synced to PCC) once the order is placed. */
export const createPatientSchedulePlan = (
  examPlan: InternalExaminationPlan,
  note?: TestOrderNote,
): AppThunk => async dispatch => {
  const result = await PatientDiagnosisRepository.createInternalExaminationPlan(examPlan)
  dispatch(fetchPatientExaminations(examPlan?.patientId))
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))

  if (!note) return
  try {
    const newDiscussion = await PatientDiagnosisRepository.addTestOrderNote(note)
    if (newDiscussion) dispatch(updatePatientDiscussionsSuccess(newDiscussion))
  } catch (err) {
    console.error(err)
  }
}

/**
 * Orders a set of recommended tests together, then writes one progress note (synced to PCC)
 * listing the ones that went through with their reasons. `tests[i]` describes `examPlans[i]`.
 */
export const orderRecommendedTests = (
  examPlans: InternalExaminationPlan[],
  note: TestOrderNote,
): AppThunk => async dispatch => {
  const results = await Promise.allSettled(
    examPlans.map(examPlan => PatientDiagnosisRepository.createInternalExaminationPlan(examPlan)),
  )
  dispatch(fetchPatientExaminations(note.patientId))

  const ordered = note.tests.filter((_, i) => results[i].status === 'fulfilled')
  if (ordered.length === 0) return
  try {
    const newDiscussion = await PatientDiagnosisRepository.addTestOrderNote({
      ...note,
      tests: ordered,
    })
    if (newDiscussion) dispatch(updatePatientDiscussionsSuccess(newDiscussion))
  } catch (err) {
    console.error(err)
  }
}

type NoteDiagnosis = NonNullable<TestOrderNote['patientDiagnoses']>[number]

/** What happened to one plan of a batch: placed, or the server's reason it was not. */
export type ExamOrderOutcome = { ok: true } | { ok: false; error?: string }

/**
 * Orders a batch of exams from the Add Examination modal: one plan at a time, so each one sees
 * the plans placed before it, then one progress note (synced to PCC) for the ones that went
 * through. Resolves with an outcome per plan, in order; a failure does not stop the rest.
 */
export const orderExaminations = (
  examPlans: InternalExaminationPlan[],
  note: TestOrderNote,
): ThunkAction<Promise<ExamOrderOutcome[]>, RootState, null, Action<string>> => async dispatch => {
  const outcomes: ExamOrderOutcome[] = []
  for (const examPlan of examPlans) {
    try {
      const result = await PatientDiagnosisRepository.createInternalExaminationPlan(examPlan)
      if (result.data.newSchedulePlan)
        dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
      outcomes.push({ ok: true })
    } catch (err) {
      console.error(err)
      outcomes.push({ ok: false, error: (err as any)?.response?.data?.error })
    }
  }
  dispatch(fetchPatientExaminations(note.patientId))

  const ordered = note.tests.filter((_, i) => outcomes[i].ok)
  if (ordered.length > 0) {
    // Each exam has its own codes; the note lists those of every exam that went through, once.
    const patientDiagnoses = new Map<string, NoteDiagnosis>()
    examPlans
      .filter((_, i) => outcomes[i].ok)
      .forEach(plan =>
        (plan.patientDiagnoses ?? []).forEach((d: NoteDiagnosis) =>
          patientDiagnoses.set(d.diagnosisCode, d),
        ),
      )
    try {
      const newDiscussion = await PatientDiagnosisRepository.addTestOrderNote({
        ...note,
        tests: ordered,
        patientDiagnoses: Array.from(patientDiagnoses.values()),
      })
      if (newDiscussion) dispatch(updatePatientDiscussionsSuccess(newDiscussion))
    } catch (err) {
      console.error(err)
    }
  }
  return outcomes
}

/** Edit = the server closes the old plan (cancelling its future tests) and creates a new one. */
export const editPatientSchedulePlan = (
  examPlanId: string,
  examPlan: InternalExaminationPlan,
): AppThunk => async dispatch => {
  const result = await PatientDiagnosisRepository.editInternalExaminationPlan(examPlanId, examPlan)
  if (result.data.closedSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.closedSchedulePlan))
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
  dispatch(fetchPatientExaminations(examPlan.patientId))
}

export const closePatientSchedulePlan = (
  examPlanId: string,
  followUp: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(addDiscussionToTopic(followUp))

  const result = await PatientDiagnosisRepository.closeInternalExaminationPlan(
    examPlanId,
    followUp.discussion,
  )
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
  // Closing cancels the plan's future tests, so the examinations list changes too.
  dispatch(fetchPatientExaminations(followUp.patientId))
}

/** Cancels an active plan (its future, not-performed tests) with no comment or discussion entry. */
export const cancelPatientSchedulePlan = (
  examPlanId: string,
  patientId: string,
): AppThunk => async dispatch => {
  const result = await PatientDiagnosisRepository.closeInternalExaminationPlan(examPlanId)
  if (result.data.newSchedulePlan)
    dispatch(updatePatientSchedulePlanSuccess(result.data.newSchedulePlan))
  dispatch(fetchPatientExaminations(patientId))
}

export const updateTopicStatus = (topicId: string, status: string): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateTopicStatus(topicId, status)

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addDiagnosisToTopic = (
  topicId: string,
  diagnosisToAdd: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.addDiagnosisToTopic(topicId, diagnosisToAdd)

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const mergeDiagnosisToTopic = (
  topicId: string,
  mergeTopics: PatientsDiagnosis,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.mergeTopics(topicId, mergeTopics)
    console.log(result)
    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const deleteTopic = (topicId: string): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    await PatientDiagnosisRepository.deleteTopic(topicId)

    dispatch(deleteTopicSuccess({ id: topicId }))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updateDiagnosisInTopic = (
  topicId: string,
  diagnosisToUpdate: PatientsDiagnosisSimple,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateDiagnosisInTopic(
      topicId,
      diagnosisToUpdate,
    )

    dispatch(updatePatientTopicSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addDiscussionToTopic = (
  discussionToAdd: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())
  try {
    const newDiscussion = await PatientDiagnosisRepository.addDiscussionToTopic(discussionToAdd)
    dispatch(updatePatientDiscussionsSuccess(newDiscussion))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updateDiscussionInTopic = (
  discussionToUpdate: PatientDiagnosisDiscussions,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const result = await PatientDiagnosisRepository.updateDiscussionInTopic(discussionToUpdate)
    dispatch(updatePatientDiscussionsSuccess(result))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addMedicationToTopic = (
  medicationToAdd: PatientsDiagnosisMedications,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())
  try {
    const newMedication = await PatientDiagnosisRepository.addMedicationToTopic(medicationToAdd)
    dispatch(updatePatientMedicasionsSuccess(newMedication))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const addAllergy = (
  allergyToAdd: Allergy,
  onSuccess?: () => void,
): AppThunk => async dispatch => {
  try {
    const newAllergy = await PatientDiagnosisRepository.addAllergy(allergyToAdd)
    dispatch(updatePatientAllergiesSuccess(newAllergy))
    if (onSuccess) {
      onSuccess()
    }
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updatePatientTopic = (
  topic: PatientsDiagnosisTopics,
  onSuccess?: (patientsDiagnosisTopics: PatientsDiagnosisTopics) => void,
  onError?: (error: any) => void,
): AppThunk => async dispatch => {
  dispatch(updatePatientTopicStart())

  try {
    const updatedPatientDiagnosis = await PatientDiagnosisRepository.saveOrUpdate(topic)

    dispatch(updatePatientTopicSuccess(updatedPatientDiagnosis as PatientsDiagnosisTopics))
    if (onSuccess) {
      onSuccess(updatedPatientDiagnosis as PatientsDiagnosisTopics)
    }
  } catch (error) {
    dispatch(updatePatientFailed())

    if (onError) {
      onError(error)
    }
  }
}

export const updatePatientMedication = (
  medication: PatientsDiagnosisMedications,
): AppThunk => async dispatch => {
  try {
    const updatedPatientMedication = await PatientDiagnosisRepository.updateMedication(medication)
    dispatch(
      updatePatientMedicasionsSuccess(updatedPatientMedication as PatientsDiagnosisMedications),
    )
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export const updatePatientAllergy = (allergy: Allergy): AppThunk => async dispatch => {
  try {
    const updatedPatientAllergy = await PatientDiagnosisRepository.updateAllergy(allergy)
    dispatch(updatePatientAllergiesSuccess(updatedPatientAllergy as Allergy))
  } catch (error) {
    dispatch(updatePatientFailed())
  }
}

export default patientDiagnosisSlice.reducer
